define('presentation/signupPricingTable/viewModels/signupPricingTableViewModel',[
    'i18next',
    'businessServices/converters/currencyFormatter',
    'common/promises/promiseFactory',
    'constants/billingPackageTypeConstants',
    'presentation/signupPackagePicker/presentationObjects/signupPackagePresentationObject'
], function () {
    return function () {
        let self = this;

        const CurrencyFormatterConstructor = require('businessServices/converters/currencyFormatter');
        const _currencyFormatter = new CurrencyFormatterConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const BillingPackagePresentationObjectConstructor = require('presentation/signupPackagePicker/presentationObjects/signupPackagePresentationObject');

        const _i18n = require('i18next');
        const _billingPackageTypes = require('constants/billingPackageTypeConstants');

        const DEFAULT_BASE_VISIBLE_COUNT = 5;
        const BASE_VISIBLE_COUNT_BY_TYPE = {
            [_billingPackageTypes.liveReceptionist.id]: 6,
            [_billingPackageTypes.webChat.id]: 5,
            [_billingPackageTypes.outboundCalling.id]: 5
        };

        const UNLIMITED_VALUE = 999999;
        const UNLIMITED_I18N_VALUE = _i18n.t('plans:unlimited');
        const _getReadableValue = (value) => value === UNLIMITED_VALUE ? UNLIMITED_I18N_VALUE : value;

        const _formatIncludedItemsName = (billingPackage) => {
            switch (billingPackage.type) {
                case _billingPackageTypes.liveReceptionist.id:
                    return billingPackage.includedMinutes === 0 ?
                        _i18n.t('signupPricingTable:labelPayAsYouGo') :
                        _i18n.t('signupPlans:includedMinutes', {count: _getReadableValue(billingPackage.includedMinutes)});
                case _billingPackageTypes.webChat.id:
                    return billingPackage.includedChatSessions === 0 ?
                        _i18n.t('signupPricingTable:labelPayAsYouGo') :
                        _i18n.t('signupPlans:includedChats', {count: _getReadableValue(billingPackage.includedChatSessions)});
                case _billingPackageTypes.outboundCalling.id:
                    return billingPackage.includedOutboundContacts === 0 ?
                        _i18n.t('signupPricingTable:labelPayAsYouGo') :
                        _i18n.t('signupPlans:includedOutboundContacts', {count: _getReadableValue(billingPackage.includedOutboundContacts)});
                default:
                    return billingPackage.name;
            }
        };

        const _prepareBillingPackageForDisplay = (billingPackage) => {
            const billingPackagePresentationObject = new BillingPackagePresentationObjectConstructor();
            billingPackagePresentationObject.billingPackageId = billingPackage.billingPackageId;
            billingPackagePresentationObject.packageId = billingPackage.packageId;
            billingPackagePresentationObject.type = billingPackage.type;
            billingPackagePresentationObject.sequence = billingPackage.sequence;
            billingPackagePresentationObject.name = billingPackage.name;
            billingPackagePresentationObject.planLabel = _formatIncludedItemsName(billingPackage);
            billingPackagePresentationObject.description = billingPackage.description;
            billingPackagePresentationObject.price = _currencyFormatter.formatPlanPrice(billingPackage.pricePerMonth);
            billingPackagePresentationObject.pricePerMonth = _i18n.t('signupPackagePicker:packageFeatures.pricePerMonth', {
                price: _currencyFormatter.formatUSDForDisplay(billingPackage.pricePerMonth)
            });

            billingPackagePresentationObject.isMostPopular = billingPackage.isMostPopular;
            billingPackagePresentationObject.isOnboardingRequired = billingPackage.isOnboardingRequired;
            billingPackagePresentationObject.isRetired = billingPackage.isRetired;
            billingPackagePresentationObject.isUpgradable = billingPackage.isUpgradable;
            billingPackagePresentationObject.isSignatureService = billingPackage.isSignatureService;

            if (billingPackage.includedChatSessions != null) {
                billingPackagePresentationObject.includedChatSessions = billingPackage.includedChatSessions;
                billingPackagePresentationObject.pricePerAdditionalChatSession = _currencyFormatter.formatUSDForDisplay(billingPackage.pricePerAdditionalChatSession);
            }

            if (billingPackage.includedOutboundContacts != null) {
                billingPackagePresentationObject.includedOutboundContacts = billingPackage.includedOutboundContacts;
                billingPackagePresentationObject.pricePerAdditionalOutboundContact = _currencyFormatter.formatUSDForDisplay(billingPackage.pricePerAdditionalOutboundContact);
            }

            if (billingPackage.includedMinutes != null) {
                billingPackagePresentationObject.includedMinutes = billingPackage.includedMinutes;
                billingPackagePresentationObject.pricePerAdditionalMinute = _currencyFormatter.formatUSDForDisplay(billingPackage.pricePerAdditionalMinute);
            }

            billingPackagePresentationObject.outboundCallingSetupFee = _currencyFormatter.formatUSDForDisplay(billingPackage.outboundCallingSetupFee);

            billingPackagePresentationObject.isSelected = ko.pureComputed(() => {
                return self.selectedPackageId() === billingPackage.packageId;
            });

            billingPackagePresentationObject.isSelectable = ko.pureComputed(() => {
                return billingPackage.type !== _billingPackageTypes.telephony.id;
            });

            billingPackagePresentationObject.additionalPriceDisplay = ko.pureComputed(() => {
                switch (billingPackage.type) {
                    case _billingPackageTypes.liveReceptionist.id:
                        return billingPackagePresentationObject.pricePerAdditionalMinute;
                    case _billingPackageTypes.webChat.id:
                        return billingPackagePresentationObject.pricePerAdditionalChatSession;
                    case _billingPackageTypes.outboundCalling.id:
                        return billingPackagePresentationObject.pricePerAdditionalOutboundContact;
                    default:
                        return null;
                }
            });

            billingPackagePresentationObject.selectLabel = ko.pureComputed(() => {
                return _i18n.t(billingPackagePresentationObject.isSelected() ? 'signupPricingTable:labelSelected' : 'signupPricingTable:labelSelect');
            });

            return billingPackagePresentationObject;
        };

        self.onSelectCallback = null;
        self.onCustomCallback = null;
        self.packageType = ko.observable(null);
        self.availablePackages = ko.observableArray([]);
        self.selectedPackageId = ko.observable(null);
        self.isExpanded = ko.observable(false);

        self.additionalColumnLabel = ko.pureComputed(() => {
            switch (self.packageType()) {
                case _billingPackageTypes.liveReceptionist.id:
                    return _i18n.t("signupPricingTable:colAdditional", {context: _billingPackageTypes.liveReceptionist.key});
                case _billingPackageTypes.webChat.id:
                    return _i18n.t("signupPricingTable:colAdditional", {context: _billingPackageTypes.webChat.key});
                case _billingPackageTypes.outboundCalling.id:
                    return _i18n.t("signupPricingTable:colAdditional", {context: _billingPackageTypes.outboundCalling.key});
                default:
                    return _i18n.t("signupPricingTable:colAdditional");
            }
        });

        self.displayPackages = ko.pureComputed(() => {
            return self.availablePackages()
                .slice()
                .sort((a, b) => a.sequence - b.sequence);
        });

        self.baseVisibleCount = ko.pureComputed(() => {
            return BASE_VISIBLE_COUNT_BY_TYPE[self.packageType()] ? BASE_VISIBLE_COUNT_BY_TYPE[self.packageType()] : DEFAULT_BASE_VISIBLE_COUNT;
        });

        self.basePackages = ko.pureComputed(() => self.displayPackages().slice(0, self.baseVisibleCount()));
        self.extraPackages = ko.pureComputed(() => self.displayPackages().slice(self.baseVisibleCount()));

        self.hasExtraPackages = ko.pureComputed(() => self.displayPackages().length > self.baseVisibleCount());

        self.showOutboundCallingSetupFee = ko.pureComputed(() => {
            return self.packageType() === _billingPackageTypes.outboundCalling.id;
        });

        self.toggleSelectedPackage = (billingPackage) => {
            self.selectedPackageId(billingPackage.packageId);
            if (self.onSelectCallback) {
                self.onSelectCallback(billingPackage);
            }
        };

        self.toggleShowMorePackages = () => {
            self.isExpanded(!self.isExpanded());
        };

        self.onNeedLargerPlanSelected = () => {
            if (self.onCustomCallback) {
                self.onCustomCallback();
            }
        };

        self.activate = ({ packageType, packageId, availablePackages, onSelectCallback = null, onCustomCallback = null}) => {
            return _initialize(packageType, packageId, availablePackages, onSelectCallback, onCustomCallback);
        };

        const _initialize = (packageType, packageId, availablePackages, onSelectCallback, onCustomCallback) => {
            self.packageType(ko.unwrap(packageType));

            if (ko.isObservable(packageId)) {
                self.selectedPackageId = packageId;
            } else {
                self.selectedPackageId(packageId);
            }

            self.onSelectCallback = onSelectCallback;
            self.onCustomCallback = onCustomCallback;

            const displayBillingPackages = ko.unwrap(availablePackages).map((p) => _prepareBillingPackageForDisplay(p));
            self.availablePackages(displayBillingPackages);
            self.isExpanded(false);

            return _promiseFactory.wait();
        };
    };
});

